import * as React from 'react';
import { useState } from 'react';
import {
  AppBar,
  Box,
  Chip,
  Container,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import MenuIcon from '@mui/icons-material/Menu';
import { brandingFixture } from '../lib/branding/branding-fixture';
import { useAppSelector } from '../store/hooks';
import { selectIsAdmin } from '../store/selectors/admin-selectors';
import { SideNav } from './SideNav';
import { SkipNavigation } from './SkipNavigation';

const DRAWER_WIDTH = 240;

interface AppShellProps {
  readonly children: React.ReactNode;
}

/**
 * Public-website chrome shell per design §3.2 / §7 / Requirement 2 / R8.7.
 *
 * Layout:
 * - SkipNavigation link (Requirement 11.1) as the first focusable element.
 * - MUI AppBar with the brand logo + a MenuIcon toggle on viewports < 900px.
 * - MUI Drawer:
 *     - `permanent` variant on viewports ≥ 900px (lg+).
 *     - `temporary` variant on viewports < 900px, controlled by a useState toggle.
 * - <main id="main-content"> wrapping the route children — the SkipNavigation
 *   link's anchor target.
 * - Footer rendering BrandingConfig.copyright per Property 9 / R8.7.
 *
 * Brand assets come from the branding fixture (`logos.primary`,
 * `copyright`) per Property 9 — until @dvgup/branding scaffolds.
 */
export function AppShell({ children }: AppShellProps): JSX.Element {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md')); // 900px = md breakpoint
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = useAppSelector(selectIsAdmin);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SkipNavigation targetId="main-content" />
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar sx={{ gap: 2 }}>
          {!isDesktop && (
            <IconButton
              color="inherit"
              aria-label="open navigation drawer"
              edge="start"
              onClick={() => setMobileOpen((open) => !open)}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Box
            component="img"
            src={brandingFixture.logos.primary}
            alt="DVGUP"
            sx={{ height: 32 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          {isAdmin && (
            <Chip
              icon={<AdminPanelSettingsIcon sx={{ color: 'inherit !important' }} />}
              label="Admin mode"
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.18)',
                color: 'common.white',
                fontWeight: 600,
                '& .MuiChip-icon': { color: 'common.white' },
              }}
            />
          )}
        </Toolbar>
      </AppBar>
      <Drawer
        variant={isDesktop ? 'permanent' : 'temporary'}
        open={isDesktop ? true : mobileOpen}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
        ModalProps={{ keepMounted: true }}
      >
        <Toolbar />
        <SideNav />
      </Drawer>
      <Box
        component="main"
        id="main-content"
        sx={{
          flexGrow: 1,
          p: 3,
          mt: 8,
          ml: isDesktop ? `${DRAWER_WIDTH}px` : 0,
        }}
      >
        <Container maxWidth="lg">{children}</Container>
      </Box>
      <Box component="footer" sx={{ p: 2, textAlign: 'center', mt: 'auto' }}>
        <Typography variant="body2" color="text.secondary">
          {brandingFixture.copyright}
        </Typography>
      </Box>
    </Box>
  );
}
